// src/services/mizanService.js
import { EDEFTER_API_URL } from '../config/edefterApi';
import AuthService from './authService';

class MizanService {
  /**
   * Mizan dosyasını yükle ve parse et
   * @param {File} file - Yüklenecek dosya (Excel veya PDF)
   * @param {string} userId - Kullanıcı ID
   * @param {string} companyId - Şirket ID
   * @param {string} taxId - Vergi numarası
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  static async uploadMizan(file, userId, companyId, taxId) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('userId', userId);
      formData.append('companyId', companyId);
      formData.append('taxId', taxId);

      const response = await AuthService.authenticatedFetch(`${EDEFTER_API_URL}/api/mizan/upload`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (response.ok) {
        return {
          success: true,
          data: data,
        };
      } else if (response.status === 409) {
        // Conflict - Mizan zaten mevcut
        return {
          success: false,
          error: data || 'Bu mizan veritabanında zaten mevcut.',
        };
      } else {
        return {
          success: false,
          error: data || 'Mizan yüklenirken bir hata oluştu.',
        };
      }
    } catch (error) {
      console.error('Mizan upload error:', error);
      return {
        success: false,
        error: error.message || 'Mizan yüklenirken bir hata oluştu.',
      };
    }
  }

  /**
   * Şirkete ait yüklü mizan belgelerini getir
   * @param {string} companyId - Şirket ID
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  static async getMizanBelgelerByCompany(companyId) {
    try {
      const response = await AuthService.authenticatedFetch(`${EDEFTER_API_URL}/api/mizan/belgeler/${companyId}`);
      
      if (response.ok) {
        const data = await response.json();
        return {
          success: true,
          data: data,
        };
      } else {
        return {
          success: false,
          error: 'Mizan belgeleri yüklenirken bir hata oluştu.',
        };
      }
    } catch (error) {
      console.error('Get mizan belgeler error:', error);
      return {
        success: false,
        error: error.message || 'Mizan belgeleri yüklenirken bir hata oluştu.',
      };
    }
  }

  static async getPeriodMizansByIdentifier(identifier, type = 'TRIAL_BALANCE_UPLOAD') {
    try {
      const response = await AuthService.authenticatedFetch(
        `${EDEFTER_API_URL}/api/mizan/period-belgeler?identifier=${encodeURIComponent(identifier)}&type=${encodeURIComponent(type)}`
      );
      if (response.ok) {
        const data = await response.json();
        return { success: true, data };
      }
      return { success: false, error: 'Period mizan belgeleri yüklenirken bir hata oluştu.' };
    } catch (error) {
      console.error('Get period mizan belgeler error:', error);
      return { success: false, error: error.message || 'Period mizan belgeleri yüklenirken bir hata oluştu.' };
    }
  }

  /**
   * Mizan ID'ye göre mizan detaylarını getir
   * @param {string} mizanId - Mizan ID
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  static async getMizanById(mizanId) {
    try {
      const response = await AuthService.authenticatedFetch(`${EDEFTER_API_URL}/api/mizan/${mizanId}`);
      
      if (response.ok) {
        const data = await response.json();
        return {
          success: true,
          data: data,
        };
      } else if (response.status === 404) {
        return {
          success: false,
          error: 'Mizan bulunamadı.',
        };
      } else {
        return {
          success: false,
          error: 'Mizan yüklenirken bir hata oluştu.',
        };
      }
    } catch (error) {
      console.error('Get mizan by id error:', error);
      return {
        success: false,
        error: error.message || 'Mizan yüklenirken bir hata oluştu.',
      };
    }
  }

  static async getPeriodMizanById(periodMizanId) {
    try {
      const response = await AuthService.authenticatedFetch(`${EDEFTER_API_URL}/api/mizan/period/${periodMizanId}`);
      if (response.ok) {
        const data = await response.json();
        return { success: true, data };
      } else if (response.status === 404) {
        return { success: false, error: 'Mizan bulunamadı.' };
      }
      return { success: false, error: 'Mizan yüklenirken bir hata oluştu.' };
    } catch (error) {
      console.error('Get period mizan by id error:', error);
      return { success: false, error: error.message || 'Mizan yüklenirken bir hata oluştu.' };
    }
  }

  /**
   * Mizan dosyasını indir
   * @param {string} companyId - Şirket ID
   * @param {string} mizanId - Mizan ID
   * @param {string} timestamp - Timestamp
   * @returns {Promise<{success: boolean, blob?: Blob, error?: string}>}
   */
  static async downloadMizan(companyId, mizanId, timestamp) {
    try {
      const response = await AuthService.authenticatedFetch(
        `${EDEFTER_API_URL}/api/mizan/download?companyId=${companyId}&mizanId=${mizanId}&timestamp=${timestamp}`
      );
      
      if (response.ok) {
        const blob = await response.blob();
        return {
          success: true,
          blob: blob,
        };
      } else if (response.status === 404) {
        return {
          success: false,
          error: 'Dosya bulunamadı.',
        };
      } else {
        return {
          success: false,
          error: 'Dosya indirilirken bir hata oluştu.',
        };
      }
    } catch (error) {
      console.error('Download mizan error:', error);
      return {
        success: false,
        error: error.message || 'Dosya indirilirken bir hata oluştu.',
      };
    }
  }

  /**
   * Mizan'ı aktar (bilanço ve gelir tablosuna aktar)
   * @param {string} mizanId - Mizan ID
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  static async aktarMizan(mizanId) {
    try {
      const response = await AuthService.authenticatedFetch(`${EDEFTER_API_URL}/api/mizan/aktar/${mizanId}`, {
        method: 'POST',
      });
      
      if (response.ok) {
        return {
          success: true,
        };
      } else if (response.status === 404) {
        return {
          success: false,
          error: 'Mizan bulunamadı.',
        };
      } else {
        return {
          success: false,
          error: 'Mizan aktarılırken bir hata oluştu.',
        };
      }
    } catch (error) {
      console.error('Aktar mizan error:', error);
      return {
        success: false,
        error: error.message || 'Mizan aktarılırken bir hata oluştu.',
      };
    }
  }

  /**
   * Mizan'ı sil
   * @param {string} mizanId - Mizan ID
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  static async deleteMizan(mizanId) {
    try {
      const response = await AuthService.authenticatedFetch(`${EDEFTER_API_URL}/api/mizan/${mizanId}`, {
        method: 'DELETE',
      });
      
      if (response.ok) {
        return {
          success: true,
        };
      } else if (response.status === 404) {
        return {
          success: false,
          error: 'Mizan bulunamadı.',
        };
      } else {
        const errorText = await response.text();
        return {
          success: false,
          error: errorText || 'Mizan silinirken bir hata oluştu.',
        };
      }
    } catch (error) {
      console.error('Delete mizan error:', error);
      return {
        success: false,
        error: error.message || 'Mizan silinirken bir hata oluştu.',
      };
    }
  }

  static async deletePeriodMizan(periodMizanId) {
    try {
      const response = await AuthService.authenticatedFetch(`${EDEFTER_API_URL}/period-mizan/${periodMizanId}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        return { success: true };
      } else if (response.status === 404) {
        return { success: false, error: 'Mizan bulunamadı.' };
      }
      const errorText = await response.text();
      return { success: false, error: errorText || 'Mizan silinirken bir hata oluştu.' };
    } catch (error) {
      console.error('Delete period mizan error:', error);
      return { success: false, error: error.message || 'Mizan silinirken bir hata oluştu.' };
    }
  }
}

export default MizanService;

